import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { getAdminTripDetail, listTripTrackingBookings } from "../../../services/tripTrackingApi";
import { formatDateDMY, formatDateTimeDMY12 } from "../../../utils/dateFormat";
import { vehicleTripDates } from "../../../utils/vehicleTripDates";
import { buildTripMapModel } from "../../../components/tripMap/tripMapModel";
import { AdminTripOverview, OwnerTripMap } from "./TripMapPanels";

const ODOMETER_EVENT_TYPES = ["TRIP_STARTED", "TRIP_ENDED"];

// Plain words for the reasons the driver app reports. "STALLED" on a screen tells the office
// nothing; "sharing was on but nothing was recorded" tells them what to ask the driver.
const SHARING_STOPPED_LABELS = {
  DRIVER_STOPPED: "Driver switched sharing off",
  TRIP_ENDED: "Trip was ended",
  TRIP_EXPIRED: "Closed automatically - past the trip's last day",
  TRIP_REMOVED: "Driver removed the trip from the app",
  LOCATION_OFF: "Phone location was switched off",
  STALLED: "Sharing was on but nothing was being recorded",
};

// Only two of these are normal. The rest mean the route stopped before the trip did.
const SHARING_STOPPED_OK = new Set(["DRIVER_STOPPED", "TRIP_ENDED", "TRIP_EXPIRED"]);

const EVENT_LABELS = {
  DRIVER_REACHED_PICKUP: "Driver Reached Pickup",
  TRIP_STARTED: "Trip Started",
  DAY_START: "Day Start",
  DROPPED: "Dropped",
  PICKED_UP: "Picked Up",
  DAY_END: "Day End",
  TRIP_ENDED: "Trip Ended",
  HOTEL_NOTIFIED: "Called Hotel",
};

export default function TripTrackingNew() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState({ key: "travelDate", dir: "desc" });
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);

  const load = async ({ silent = false, keepBookingId = null } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await listTripTrackingBookings();
      const nextBookings = buildBookingRows(data.bookings, data.trackingRows);
      setBookings(nextBookings);
      if (keepBookingId) {
        setSelected(nextBookings.find((booking) => booking.bookingId === keepBookingId) || null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load trip tracking");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const updateWidth = () => setTableScrollWidth(tableRef.current?.scrollWidth || 0);
    updateWidth();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateWidth);
    if (tableRef.current) observer?.observe(tableRef.current);
    window.addEventListener("resize", updateWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [loading, bookings]);

  const syncTableScroll = (event) => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const syncTopScroll = (event) => {
    if (topScrollRef.current) topScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const statusCounts = useMemo(() => {
    const counts = new Map();
    bookings.forEach((booking) => {
      const status = normalizedTripStatus(booking.tripStatus);
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return counts;
  }, [bookings]);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    const statusMatched = statusFilter === "ALL"
      ? bookings
      : bookings.filter((booking) => normalizedTripStatus(booking.tripStatus) === statusFilter);
    const matched = !text ? statusMatched : statusMatched.filter((booking) =>
      [
        booking.bookingId,
        booking.tripTitle,
        booking.tripStatus,
        booking.travelDate,
        booking.customerName,
        booking.customerPhone,
        booking.driverName,
        booking.driverPhone,
        booking.vehicleName,
        booking.vehicleRegistrationNumber,
        booking.events.map((event) => event.placeLabel).join(" "),
        booking.gpsAuditText,
      ]
        .join(" ")
        .toLowerCase()
        .includes(text)
    );
    return sortTrackingRows(matched, sort.key, sort.dir);
  }, [bookings, query, sort, statusFilter]);

  const requestSort = (key) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <>
      <Card className="tracking-modern-card shadow-sm">
        <Card.Header className="tracking-modern-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <div>
            <div className="tracking-modern-title">Trip Tracking</div>
            <div className="small text-muted">All bookings with driver tracking progress and IST timestamps.</div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <InputGroup size="sm" style={{ minWidth: 230 }}>
              <InputGroup.Text>Status</InputGroup.Text>
              <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="ALL">All Statuses ({bookings.length})</option>
                {[...statusCounts.entries()]
                  .sort(([left], [right]) => left.localeCompare(right))
                  .map(([status, count]) => (
                    <option key={status} value={status}>{statusLabel(status)} ({count})</option>
                  ))}
              </Form.Select>
            </InputGroup>
            <InputGroup size="sm">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="booking, trip, driver, vehicle"
              />
            </InputGroup>
            <Button size="sm" variant="outline-secondary" onClick={load}>Refresh</Button>
          </div>
        </Card.Header>
        {loading ? (
          <div className="py-5 text-center">
            <Spinner />
            <div className="small text-muted mt-2">Loading bookings...</div>
          </div>
        ) : (
          <>
          <div
            ref={topScrollRef}
            className="booking-table-top-scroll"
            onScroll={syncTableScroll}
            aria-label="Trip tracking table horizontal scroll"
          >
            <div style={{ width: tableScrollWidth, height: 1 }} />
          </div>
          <div ref={tableScrollRef} className="table-responsive" onScroll={syncTopScroll}>
            <Table ref={tableRef} hover className="tracking-modern-table mb-0 align-middle">
              <thead>
                <tr><th style={{ width: 44 }}>#</th>
                  <TrackingSortableTh label="Booking" sortKey="bookingId" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Trip" sortKey="tripTitle" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Travel Date" sortKey="travelDate" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Customer" sortKey="customerName" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Driver" sortKey="driverName" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Vehicle" sortKey="vehicleName" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Status" sortKey="tripStatus" sort={sort} onSort={requestSort} />
                  <TrackingSortableTh label="Tracking" sortKey="eventCount" sort={sort} onSort={requestSort} />
                  <th>Driver WhatsApp</th>
                  <th>Customer WhatsApp</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((booking, index) => (
                  <tr key={booking.bookingId} className={`tracking-row tracking-row-${normalizedTripStatus(booking.tripStatus).toLowerCase()}`}><td className="small fw-semibold">{index + 1}</td>
                    <td className="text-nowrap"><span className="tracking-id-chip">#{booking.bookingId}</span></td>
                    <td style={{ minWidth: 220 }}>
                      <div className="fw-semibold">{booking.tripTitle || "-"}</div>
                      {booking.noOfTravellers ? (
                        <div className="small text-muted">{booking.noOfTravellers} travellers</div>
                      ) : null}
                    </td>
                    <td className="text-nowrap">{formatDateDMY(booking.travelDate, "-")}</td>
                    <td style={{ minWidth: 180 }}>
                      <div>{booking.customerName || "-"}</div>
                      <div className="small text-muted">{booking.customerPhone || "-"}</div>
                      {booking.plannedByPhone && (
                        <div className="small text-muted">Planned by: {booking.plannedByName || "-"} | {booking.plannedByPhone}</div>
                      )}
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <div>{booking.driverName || "-"}</div>
                      <div className="small text-muted">{booking.driverPhone || "-"}</div>
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <div>{booking.vehicleName || "-"}</div>
                      <div className="small text-muted">{booking.vehicleRegistrationNumber || "-"}</div>
                    </td>
                    <td><Badge bg={statusVariant(booking.tripStatus)}>{booking.tripStatus || "PENDING"}</Badge></td>
                    <td>
                      <TrackingSummary booking={booking} />
                    </td>
                    <td className="text-nowrap">
                      <div className="d-flex flex-column gap-2">
                        <CopyWhatsAppButton
                          text={booking.driverWhatsappText}
                          label="Copy Driver Message"
                          successMessage="Driver WhatsApp text copied"
                        />
                        <CopyDayWiseImageButton booking={booking} />
                      </div>
                    </td>
                    <td className="text-nowrap">
                      <CopyWhatsAppButton
                        text={booking.customerWhatsappText}
                        label="Copy Customer Message"
                        successMessage="Customer WhatsApp text copied"
                      />
                    </td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" onClick={() => setSelected(booking)}>
                        View Timeline
                      </Button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={12} className="text-center text-muted py-4">No bookings found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          </>
        )}
      </Card>

      <TimelineModal
        booking={selected}
        onHide={() => setSelected(null)}
        onRefresh={() => load({ silent: true, keepBookingId: selected?.bookingId })}
        refreshing={refreshing}
      />
    </>
  );
}

export function TrackingSummary({ booking }) {
  const total = booking.events.length;
  const last = booking.events[booking.events.length - 1];
  const audit = gpsAuditSummary(booking);
  return (
    <div>
      <Badge bg={total ? "success" : "secondary"}>{total} events</Badge>
      <Badge bg={audit.variant} className="ms-1">{audit.label}</Badge>
      <div className="small text-muted mt-1">
        {last ? `Last: ${EVENT_LABELS[last.eventType] || last.eventType} at ${formatDateTimeDMY12(last.capturedAtIst, "-")}` : "No tracking yet"}
      </div>
      <div className="small text-muted">
        Route pings: {booking.driverLocationPingCount || 0}
        {booking.lastDriverLocationAtIst ? ` | Last GPS: ${formatDateTimeDMY12(booking.lastDriverLocationAtIst, "-")}` : ""}
      </div>
    </div>
  );
}

// variant "admin" shows the whole-trip overview; "owner" (transport owner portal) shows the day map
// with its place list and keeps the detailed timeline folded away. loadDetail(bookingId) fetches the
// trip; without it the admin route is used.
export function TimelineModal({ booking, onHide, onRefresh, refreshing, showPublicActions = true, variant = "admin", loadDetail }) {
  const [detail, setDetail] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const bookingId = booking?.bookingId;
  const loadDetailRef = useRef(loadDetail);
  loadDetailRef.current = loadDetail;
  const requestRef = useRef(0);

  // A refresh keeps the map on screen until the new answer arrives; only a different booking clears it.
  const reloadDetail = useCallback(async () => {
    if (!bookingId) return;
    const request = ++requestRef.current;
    setRouteLoading(true);
    try {
      const loader = loadDetailRef.current || getAdminTripDetail;
      const result = await loader(bookingId);
      if (request !== requestRef.current) return;
      setDetail(result || null);
      setNow(new Date());
    } catch (error) {
      // Keep whatever is already shown.
    } finally {
      if (request === requestRef.current) setRouteLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    setDetail(null);
    setShowFullTimeline(false);
    reloadDetail();
  }, [reloadDetail]);

  const model = useMemo(() => buildTripMapModel(detail, now), [detail, now]);
  const routePings = detail?.driverLocations?.length || 0;

  const refreshAll = async () => {
    reloadDetail();
    await onRefresh?.();
  };

  return (
    <Modal
      show={Boolean(booking)}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      dialogClassName={variant === "owner" ? undefined : "trip-tracking-modal"}
    >
      <Modal.Header closeButton>
        <Modal.Title>Trip Tracking Timeline</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {booking && (
          <>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <div>
                <div className="fw-semibold">#{booking.bookingId} - {booking.tripTitle || "Trip"}</div>
                <div className="small text-muted">
                  Travel: {formatDateDMY(booking.travelDate, "-")} | Driver: {booking.driverName || "-"} | Vehicle: {booking.vehicleName || "-"}
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-start">
                {showPublicActions ? <PublicLinkActions token={booking.trackingPublicToken} /> : null}
                <Badge bg={statusVariant(booking.tripStatus)} className="align-self-start">{booking.tripStatus || "PENDING"}</Badge>
              </div>
            </div>
            {/* The map does not wait for tracking timestamps: the driver app streams location
                without necessarily marking a place, and the plan is worth seeing before either. */}
            {routeLoading && !detail ? (
              <Card body className="mb-3 text-center text-muted">Loading trip map...</Card>
            ) : null}
            {model ? (
              variant === "owner"
                ? <OwnerTripMap model={model} detail={detail} now={now} />
                : <AdminTripOverview model={model} detail={detail} now={now} />
            ) : null}
            <DriverReadinessPanel booking={booking} />
            <SharingStoppedPanel
              reason={detail?.sharingStoppedReason}
              at={detail?.sharingStoppedAtIst}
              note={detail?.sharingStoppedNote}
            />
            <DistanceCheckPanel check={detail?.distanceCheck || null} />
            {variant === "owner" ? (
              <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => setShowFullTimeline((open) => !open)}>
                {showFullTimeline ? "Hide full timeline" : "Show full timeline"}
              </Button>
            ) : null}
            {variant !== "owner" || showFullTimeline ? (
              booking.events.length ? (
                <>
                  <GpsAuditPanel booking={booking} />
                  <div className="tracking-timeline tracking-timeline-readable">
                    {buildTimelineItems(booking.events).map((item, index) => (
                      <TimelineItem item={item} index={index} key={item.key} />
                    ))}
                  </div>
                </>
              ) : (
                <Card body className="text-center text-muted py-4">
                  No driver tracking timestamps have been captured for this booking.
                  {routePings > 0 ? " Location tracking is running - the route is on the map above." : ""}
                </Card>
              )
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={refreshAll} disabled={refreshing || routeLoading}>
          {refreshing || routeLoading ? "Refreshing..." : "Refresh"}
        </Button>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// Odometer kilometres against route kilometres. The odometer photos exist so an extra kilometres
// claim can be checked rather than believed, and that check is only useful if the office is shown
// it without having to work it out.
function SharingStoppedPanel({ reason, at, note }) {
  if (!reason) return null;
  const expected = SHARING_STOPPED_OK.has(reason);
  return (
    <Card body className={`mb-3 ${expected ? "border-secondary-subtle" : "border-danger"}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-semibold">Location sharing stopped</div>
          <div className={`small ${expected ? "text-muted" : "text-danger fw-semibold"}`}>
            {SHARING_STOPPED_LABELS[reason] || reason}
          </div>
          {at ? (
            <div className="small text-muted">at {formatDateTimeDMY12(at, "-")}</div>
          ) : null}
          {note ? <div className="small text-muted">{note}</div> : null}
        </div>
        <Badge bg={expected ? "secondary" : "danger"} className="align-self-start">
          {expected ? "Expected" : "Unplanned"}
        </Badge>
      </div>
    </Card>
  );
}

function DistanceCheckPanel({ check }) {
  if (!check) return null;
  const hasBoth = check.odometerKm != null && check.gpsKm != null;
  const variant = check.mismatch ? "border-danger" : "border-success-subtle";
  const sign = check.differenceKm > 0 ? "+" : "";
  return (
    <Card body className={`mb-3 ${variant}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-semibold">Distance Check</div>
          <div className="small text-muted">
            Odometer:{" "}
            {check.odometerKm != null
              ? `${check.odometerKm} km (${check.odometerStart} to ${check.odometerEnd})`
              : check.odometerStart != null
              ? `start ${check.odometerStart}, no end reading`
              : "no readings captured"}
          </div>
          <div className="small text-muted">
            GPS route: {check.gpsKm != null ? `${check.gpsKm} km` : "no route recorded"}
            {check.routePings ? ` from ${check.routePings} pings` : ""}
          </div>
          {hasBoth ? (
            <div className={`small mt-1 ${check.mismatch ? "text-danger fw-semibold" : "text-muted"}`}>
              Difference: {sign}{check.differenceKm} km ({sign}{check.differencePercent}%)
            </div>
          ) : null}
          {check.odometerPhotosPresent === false ? (
            <div className="small text-warning mt-1">
              Odometer photo missing at trip start or end - the reading has no proof behind it.
            </div>
          ) : null}
        </div>
        <Badge
          bg={check.mismatch ? "danger" : hasBoth ? "success" : "secondary"}
          className="align-self-start"
        >
          {check.mismatch ? "Check claim" : hasBoth ? "Matches route" : "Incomplete"}
        </Badge>
      </div>
      {check.mismatch ? (
        <div className="small text-muted mt-2">
          GPS under-reads a winding road, so a small overshoot is normal. This gap is large enough
          to ask about before paying extra kilometres.
        </div>
      ) : null}
    </Card>
  );
}

function TimelineItem({ item, index }) {
  if (item.type === "place") {
    return <PlaceTimelineItem item={item} index={index} />;
  }
  const event = item.event;
  return (
    <div className={`tracking-timeline-item tracking-timeline-item-${timelineSide(event.eventType)}`}>
      <div className={`tracking-timeline-marker bg-${eventVariant(event.eventType)}`}>
        {index + 1}
      </div>
      <div className={`tracking-timeline-card border-${eventVariant(event.eventType)}`}>
        <div className="d-flex flex-wrap justify-content-between gap-2 align-items-center mb-2">
          <Badge bg={eventVariant(event.eventType)}>{EVENT_LABELS[event.eventType] || event.eventType}</Badge>
          <span className="tracking-timeline-time">{formatDateTimeDMY12(event.capturedAtIst, "-")}</span>
        </div>
        {(event.dayLabel || event.dayTitle) && (
          <div className="small text-muted mb-1">
            {[event.dayLabel, event.dayTitle].filter(Boolean).join(" - ")}
          </div>
        )}
        <div className="tracking-timeline-title">{event.placeLabel || "-"}</div>
        <EventGpsAudit event={event} />
      </div>
    </div>
  );
}

function PlaceTimelineItem({ item, index }) {
  const dropped = item.dropped;
  const pickedUp = item.pickedUp;
  const reference = dropped || pickedUp;
  return (
    <div className="tracking-timeline-item tracking-timeline-item-place">
      <div className="tracking-timeline-marker bg-success">{index + 1}</div>
      <div className="tracking-timeline-card tracking-timeline-place-card">
        <div className="tracking-timeline-place-title">
          {reference?.placeLabel || "-"}
        </div>
        {(reference?.dayLabel || reference?.dayTitle) && (
          <div className="small text-muted mb-3 text-center">
            {[reference.dayLabel, reference.dayTitle].filter(Boolean).join(" - ")}
          </div>
        )}
        <div className="tracking-timeline-place-grid">
          <EventSummary event={dropped} label="Dropped" variant="success" emptyText="Drop pending" />
          <EventSummary event={pickedUp} label="Picked Up" variant="info" emptyText="Pickup pending" />
        </div>
        {dropped && pickedUp && (
          <div className="tracking-timeline-duration">
            Time spent at location: {formatDurationBetween(dropped.capturedAtIst, pickedUp.capturedAtIst)}
          </div>
        )}
      </div>
    </div>
  );
}

function EventSummary({ event, label, variant, emptyText }) {
  return (
    <div className={`tracking-timeline-event-summary border-${variant}`}>
      <Badge bg={variant}>{label}</Badge>
      <div className="tracking-timeline-time mt-2">
        {event ? formatDateTimeDMY12(event.capturedAtIst, "-") : emptyText}
      </div>
      {event ? <EventGpsAudit event={event} compact /> : null}
    </div>
  );
}

function GpsAuditPanel({ booking }) {
  const audit = gpsAuditSummary(booking);
  const [showIssues, setShowIssues] = useState(false);
  return (
    <Card body className="mb-3 border-info-subtle">
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <div className="fw-semibold">GPS Audit</div>
          <div className="small text-muted">
            {booking.eventsWithGps}/{booking.events.length} checkbox events have GPS proof.
          </div>
          <div className="small text-muted">
            Route pings: {booking.driverLocationPingCount || 0}
            {booking.lastDriverLocationAtIst ? ` | Latest route ping: ${formatDateTimeDMY12(booking.lastDriverLocationAtIst, "-")}` : ""}
          </div>
        </div>
        <Badge bg={audit.variant} className="align-self-start">{audit.label}</Badge>
      </div>
      {booking.suspiciousEvents.length ? (
        <>
          <Button
            size="sm"
            variant="outline-danger"
            className="mt-2"
            onClick={() => setShowIssues((current) => !current)}
          >
            {showIssues ? "Hide GPS Issue Details" : "Show GPS Issue Details"}
          </Button>
          {showIssues ? (
            <div className="small text-danger mt-2">
              {booking.suspiciousEvents.map((event) => `${EVENT_LABELS[event.eventType] || event.eventType} (${event.gpsIssue})`).join(", ")}
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function DriverReadinessPanel({ booking }) {
  const gpsDone = Boolean(booking?.driverGpsTestDone);
  const practiceDone = Boolean(booking?.driverPracticeCheckDone);
  const ready = gpsDone && practiceDone;
  return (
    <Card body className={`mb-3 ${ready ? "border-success-subtle" : "border-warning-subtle"}`}>
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <div className="fw-semibold">Pre-Trip Driver Readiness</div>
          <div className="small text-muted">
            GPS test: {gpsDone ? "Done" : "Pending"}
            {booking.driverGpsTestedAt ? ` | ${formatDateTimeDMY12(booking.driverGpsTestedAt, "-")}` : ""}
            {booking.driverGpsAccuracy ? ` | accuracy ${Math.round(booking.driverGpsAccuracy)} m` : ""}
            {booking.driverGpsLatitude != null && booking.driverGpsLongitude != null ? (
              <>
                {" | "}
                <a href={locationMapLink(booking.driverGpsLatitude, booking.driverGpsLongitude)} target="_blank" rel="noreferrer">Open GPS test map</a>
              </>
            ) : null}
          </div>
          <div className="small text-muted">
            Practice checkbox: {practiceDone ? "Done" : "Pending"}
            {booking.driverPracticeCheckedAt ? ` | ${formatDateTimeDMY12(booking.driverPracticeCheckedAt, "-")}` : ""}
          </div>
          {booking.driverReadinessDeviceInfo ? (
            <div className="small text-muted text-truncate" title={booking.driverReadinessDeviceInfo}>
              Device: {booking.driverReadinessDeviceInfo}
            </div>
          ) : null}
        </div>
        <Badge bg={ready ? "success" : "warning"} text={ready ? undefined : "dark"} className="align-self-start">
          {ready ? "Driver Ready" : gpsDone ? "Practice Pending" : "GPS Pending"}
        </Badge>
      </div>
    </Card>
  );
}

function EventGpsAudit({ event, compact = false }) {
  const issue = gpsIssue(event);
  const className = issue ? "text-danger" : "text-success";
  return (
    <div className={`small mt-1 ${className}`}>
      {event.latitude != null && event.longitude != null ? (
        <>
          {gpsProofLabel(event.gpsProofType)}
          {event.accuracy ? ` | accuracy ${Math.round(event.accuracy)} m` : ""}
          {event.gpsCapturedAtIst ? ` | GPS time ${formatDateTimeDMY12(event.gpsCapturedAtIst, "-")}` : ""}
          {" | "}
          <a href={locationMapLink(event.latitude, event.longitude)} target="_blank" rel="noreferrer">Open map</a>
        </>
      ) : (
        "GPS missing"
      )}
      {issue && !compact ? ` | ${issue}` : ""}
      <OdometerAudit event={event} />
    </div>
  );
}

// Start and end readings are what make the vehicle KM billed on the booking checkable, so a
// missing one is called out rather than quietly omitted.
function OdometerAudit({ event }) {
  if (!ODOMETER_EVENT_TYPES.includes(event.eventType)) return null;
  if (event.odometerReading == null && !event.odometerPhotoUrl) {
    return <div className="text-danger">Odometer not captured</div>;
  }
  return (
    <div className="text-body">
      {event.odometerReading != null
        ? `Odometer ${Number(event.odometerReading).toLocaleString("en-IN")} km`
        : "Odometer reading missing"}
      {event.odometerPhotoUrl ? (
        <>
          {" | "}
          <a href={event.odometerPhotoUrl} target="_blank" rel="noreferrer">Speedometer photo</a>
        </>
      ) : (
        " | photo missing"
      )}
    </div>
  );
}

// Distance the vehicle actually covered, for comparison against the KM the booking was priced on.
export function odometerDistance(events = []) {
  const reading = (type) => events.find(
    (event) => event.eventType === type && event.odometerReading != null,
  )?.odometerReading;
  const start = reading("TRIP_STARTED");
  const end = reading("TRIP_ENDED");
  if (start == null || end == null) return null;
  const distance = Number(end) - Number(start);
  return Number.isFinite(distance) && distance >= 0 ? distance : null;
}

function vehicleStartDate(booking) {
  let snapshot = null;
  try {
    snapshot = JSON.parse(booking.quoteSnapshot ?? booking.quote_snapshot ?? "null");
  } catch {
    snapshot = null;
  }
  return vehicleTripDates(booking.travelDate, snapshot).start || booking.travelDate;
}

export function buildBookingRows(bookings, trackingRows) {
  const eventsByBooking = new Map();
  (trackingRows || []).forEach((event) => {
    const list = eventsByBooking.get(event.bookingId) || [];
    list.push(event);
    eventsByBooking.set(event.bookingId, list);
  });

  return (bookings || [])
    .map((booking) => {
      const events = (eventsByBooking.get(booking.id) || []).sort(compareEvents);
      const driverLocationPingCount = maxEventNumber(events, "driverLocationPingCount");
      const lastDriverLocationAtIst = latestEventValue(events, "lastDriverLocationAtIst");
      const eventsWithGps = events.filter(hasGps).length;
      const suspiciousEvents = events
        .map((event) => ({ ...event, gpsIssue: gpsIssue(event) }))
        .filter((event) => event.gpsIssue);
      return {
        bookingId: booking.id,
        tripId: booking.tripId,
        tripTitle: booking.tripTitle,
        // Tracking follows the vehicle, so this is its first day - earlier than the package date
        // when an extra pre-night includes the vehicle.
        travelDate: vehicleStartDate(booking),
        pickupDateTime: booking.pickupDateTime,
        dropDateTime: booking.dropDateTime,
        tripStatus: booking.tripStatus,
        noOfTravellers: booking.noOfTravellers,
        // Trip side reaches the traveller; the planner stays visible as "Planned by".
        customerName: booking.travellingContactPhone ? (booking.travellingContactName || "Travelling guest") : booking.userName,
        customerPhone: booking.travellingContactPhone || booking.userPhone,
        plannedByName: booking.travellingContactPhone ? booking.userName : null,
        plannedByPhone: booking.travellingContactPhone ? booking.userPhone : null,
        driverId: booking.driverId,
        driverName: booking.driverName,
        driverPhone: booking.driverPhone,
        vehicleName: booking.assignedVehicleName,
        vehicleRegistrationNumber: booking.assignedVehicleRegistrationNumber,
        trackingPublicToken: booking.trackingPublicToken,
        driverGpsTestDone: booking.driverGpsTestDone,
        driverGpsLatitude: booking.driverGpsLatitude,
        driverGpsLongitude: booking.driverGpsLongitude,
        driverGpsAccuracy: booking.driverGpsAccuracy,
        driverGpsTestedAt: booking.driverGpsTestedAt,
        driverPracticeCheckDone: booking.driverPracticeCheckDone,
        driverPracticeCheckedAt: booking.driverPracticeCheckedAt,
        driverReadinessDeviceInfo: booking.driverReadinessDeviceInfo,
        driverWhatsappText: booking.driverWhatsappText,
        customerWhatsappText: booking.customerWhatsappText,
        transportPayable: booking.transportPayable,
        transportPaid: booking.transportPaid,
        transportBalance: booking.transportBalance,
        dayWisePlan: booking.dayWisePlan,
        refreshmentRoomName: booking.refreshmentRoomName,
        driverLocationPingCount,
        lastDriverLocationAtIst,
        eventsWithGps,
        suspiciousEvents,
        gpsAuditText: [
          `${eventsWithGps}/${events.length} gps`,
          `${driverLocationPingCount} route pings`,
          suspiciousEvents.map((event) => event.gpsIssue).join(" "),
        ].join(" "),
        events,
      };
    })
    .sort((left, right) => {
      const leftTime = latestTime(left.events);
      const rightTime = latestTime(right.events);
      return rightTime - leftTime || Number(right.bookingId) - Number(left.bookingId);
    });
}

function TrackingSortableTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 fw-semibold text-decoration-none text-reset text-nowrap"
        onClick={() => onSort(sortKey)}
      >
        {label} <span aria-hidden="true">{active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}</span>
      </button>
    </th>
  );
}

function sortTrackingRows(rows, key, dir) {
  const direction = dir === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const leftValue = key === "eventCount" ? left.events.length : left[key];
    const rightValue = key === "eventCount" ? right.events.length : right[key];
    const result = compareTrackingValues(leftValue, rightValue);
    return result ? result * direction : Number(right.bookingId) - Number(left.bookingId);
  });
}

function compareTrackingValues(left, right) {
  if (left == null || left === "") return right == null || right === "" ? 0 : 1;
  if (right == null || right === "") return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

function compareEvents(left, right) {
  const leftPinnedOrder = pinnedEventOrder(left.eventType);
  const rightPinnedOrder = pinnedEventOrder(right.eventType);
  if (leftPinnedOrder !== rightPinnedOrder) return leftPinnedOrder - rightPinnedOrder;
  const leftTime = new Date(left.capturedAtIst || 0).getTime();
  const rightTime = new Date(right.capturedAtIst || 0).getTime();
  return leftTime - rightTime;
}

function buildTimelineItems(events) {
  const placeEvents = new Map();
  const items = [];

  (events || []).forEach((event) => {
    if (event.eventType === "DROPPED" || event.eventType === "PICKED_UP") {
      const key = event.placeKey || event.placeLabel || "place";
      const current = placeEvents.get(key) || { type: "place", key: `place-${key}` };
      if (event.eventType === "DROPPED") current.dropped = event;
      if (event.eventType === "PICKED_UP") current.pickedUp = event;
      current.sortTime = Math.min(
        current.sortTime ?? Number.MAX_SAFE_INTEGER,
        new Date(event.capturedAtIst || 0).getTime()
      );
      placeEvents.set(key, current);
      return;
    }
    items.push({
      type: "event",
      key: `${event.eventType}-${event.placeKey}-${event.capturedAtIst}`,
      event,
      sortTime: new Date(event.capturedAtIst || 0).getTime(),
    });
  });

  placeEvents.forEach((item) => items.push(item));
  return items.sort((left, right) => {
    const leftEvent = left.event || left.dropped || left.pickedUp || {};
    const rightEvent = right.event || right.dropped || right.pickedUp || {};
    const pinned = pinnedEventOrder(leftEvent.eventType) - pinnedEventOrder(rightEvent.eventType);
    return pinned || (left.sortTime || 0) - (right.sortTime || 0);
  });
}

function pinnedEventOrder(eventType) {
  if (eventType === "DRIVER_REACHED_PICKUP") return 0;
  if (eventType === "TRIP_STARTED") return 1;
  if (eventType === "TRIP_ENDED") return 3;
  return 1;
}

function latestTime(events) {
  return Math.max(0, ...events.map((event) => new Date(event.capturedAtIst || 0).getTime()));
}

function latestEventValue(events, key) {
  return events
    .map((event) => event[key])
    .filter(Boolean)
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null;
}

function maxEventNumber(events, key) {
  return Math.max(0, ...events.map((event) => Number(event[key] || 0)).filter(Number.isFinite));
}

function hasGps(event) {
  return event?.latitude != null && event?.longitude != null;
}

function gpsIssue(event) {
  if (!hasGps(event)) return "GPS missing";
  if (event.gpsProofType === "DELAYED") return "Delayed GPS";
  if (Number(event.accuracy || 0) > 500) return "Low accuracy";
  return "";
}

function gpsProofLabel(proofType) {
  if (proofType === "AUTO") return "Marked automatically from the route";
  if (proofType === "DELAYED") return "Delayed GPS captured";
  if (proofType === "EXACT" || !proofType) return "GPS captured";
  return "GPS captured";
}

function gpsAuditSummary(booking) {
  if (!booking.events.length) return { variant: "secondary", label: "No GPS audit" };
  if (booking.suspiciousEvents.length) return { variant: "danger", label: `${booking.suspiciousEvents.length} GPS issue${booking.suspiciousEvents.length === 1 ? "" : "s"}` };
  return { variant: "success", label: "GPS OK" };
}

function locationMapLink(latitude, longitude) {
  return `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

function normalizedTripStatus(status) {
  return String(status || "PENDING").trim().toUpperCase();
}

function statusLabel(status) {
  return String(status || "PENDING")
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function statusVariant(status) {
  if (status === "COMPLETED") return "success";
  if (status === "ONGOING") return "warning";
  if (status === "UPCOMING") return "primary";
  return "secondary";
}

function eventVariant(eventType) {
  if (eventType === "DRIVER_REACHED_PICKUP") return "secondary";
  if (eventType === "TRIP_STARTED") return "primary";
  if (eventType === "PICKED_UP") return "info";
  if (eventType === "DROPPED") return "success";
  if (eventType === "DAY_END" || eventType === "TRIP_ENDED") return "dark";
  if (eventType === "HOTEL_NOTIFIED") return "warning";
  return "primary";
}

function timelineSide(eventType) {
  if (eventType === "DROPPED") return "left";
  if (eventType === "PICKED_UP") return "right";
  return "center";
}

function PublicLinkActions({ token }) {
  if (!token) return <span className="small text-muted">Not generated</span>;
  const url = `${window.location.origin}/tracking/${token}`;
  return (
    <div className="d-flex flex-wrap gap-2">
      <Button size="sm" variant="outline-secondary" as="a" href={url} target="_blank" rel="noreferrer">
        Open
      </Button>
      <Button size="sm" variant="outline-primary" onClick={() => copyTrackingLink(url)}>
        Copy Link
      </Button>
    </div>
  );
}

function copyTrackingLink(url) {
  copyText(url, "Public tracking link copied");
}

export function CopyWhatsAppButton({ text, label, successMessage, disabled = false, title = "" }) {
  return (
    <Button
      size="sm"
      variant="outline-success"
      disabled={!text || disabled}
      title={title}
      onClick={() => copyText(text, successMessage)}
    >
      {label}
    </Button>
  );
}

export function CopyDayWiseImageButton({ booking }) {
  const days = normalizeTrackingPlan(booking?.dayWisePlan, Boolean(String(booking?.refreshmentRoomName || "").trim()));
  return (
    <Button
      size="sm"
      variant="outline-primary"
      disabled={!days.length}
      onClick={() => copyDayWisePlanImage(days, booking)}
    >
      Copy Day-wise Plan Image
    </Button>
  );
}

async function copyDayWisePlanImage(days, booking) {
  let planImageBlob;
  try {
    planImageBlob = await createDayWisePlanImageBlob(days);
    if (!window.ClipboardItem || !navigator.clipboard?.write) {
      downloadImageBlob(planImageBlob, booking);
      toast.warning("Image clipboard is unavailable; plan image downloaded");
      return;
    }
    await navigator.clipboard.write([new ClipboardItem({ "image/png": planImageBlob })]);
    toast.success("Day-wise plan image copied");
  } catch {
    if (planImageBlob) {
      downloadImageBlob(planImageBlob, booking);
      toast.warning("Image could not be copied; plan image downloaded");
    } else {
      toast.error("Could not create day-wise plan image");
    }
  }
}

// Same stops the driver app lists: no meals, and Refreshments first on Day 1 only when the booking
// has a refreshment room.
const MEAL_NAMES = ["breakfast", "lunch", "dinner"];

function normalizeTrackingPlan(plan, hasRefreshmentRoom) {
  let value = plan;
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return []; }
  }
  const source = Array.isArray(value)
    ? value
    : Array.isArray(value?.days)
      ? value.days
      : Array.isArray(value?.dayWisePlan)
        ? value.dayWisePlan
        : [];
  return source.map((day, index) => {
    const locations = (Array.isArray(day?.locations) ? day.locations : [])
      .map((location) => typeof location === "string"
        ? location
        : location?.locationName || location?.name || location?.label || location?.placeName || "")
      .filter((location) => location && !MEAL_NAMES.includes(String(location).trim().toLowerCase()));
    if (index === 0 && hasRefreshmentRoom
      && !locations.some((location) => String(location).trim().toLowerCase() === "refreshments")) {
      locations.unshift("Refreshments");
    }
    return {
      day: day?.day || day?.dayLabel || `Day ${index + 1}`,
      title: day?.title || day?.dayTitle || day?.route || "",
      locations,
    };
  }).filter((day) => day.title || day.locations.length);
}

function createDayWisePlanImageBlob(days, messageText = "") {
  const columns = Math.min(3, Math.max(1, days.length));
  const columnWidth = 420;
  const gap = 30;
  const padding = 50;
  const headerHeight = 105;
  const lineHeight = 34;
  const rows = Math.ceil(days.length / columns);
  const rowHeights = Array.from({ length: rows }, (_, rowIndex) => {
    const rowDays = days.slice(rowIndex * columns, (rowIndex + 1) * columns);
    return Math.max(...rowDays.map((day) => {
      const titleLines = wrapCanvasText(day.title, 32);
      const locationLines = day.locations.reduce((count, location) => count + wrapCanvasText(location, 34).length, 0);
      return headerHeight + (titleLines.length * lineHeight) + (locationLines * lineHeight) + 45;
    }), 260);
  });
  const width = padding * 2 + columns * columnWidth + (columns - 1) * gap;
  const messageLines = String(messageText || "").split("\n").flatMap((line) => {
    const wrapped = wrapCanvasText(line.replaceAll("*", ""), 82);
    return wrapped.length ? wrapped.map((text, index) => ({ text, source: line, first: index === 0 })) : [{ text: "", source: line, first: true }];
  });
  const planHeight = rowHeights.reduce((sum, value) => sum + value, 0) + (rows - 1) * gap;
  const messageHeight = messageLines.length ? 75 + messageLines.length * 34 : 0;
  const height = padding * 2 + planHeight + messageHeight;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  let y = padding;
  for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
    const rowHeight = rowHeights[rowIndex];
    const rowY = y;
    days.slice(rowIndex * columns, (rowIndex + 1) * columns).forEach((day, columnIndex) => {
      drawDayColumn(context, day, padding + columnIndex * (columnWidth + gap), rowY, columnWidth, rowHeight);
    });
    y += rowHeight + gap;
  }
  if (messageLines.length) {
    y = padding + planHeight + 38;
    context.strokeStyle = "#d9e6ec";
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(padding, y);
    context.lineTo(width - padding, y);
    context.stroke();
    y += 42;
    messageLines.forEach((line) => {
      const source = line.source.trim();
      const heading = /^\*[^*]+\*$/.test(source);
      const keyed = source.startsWith("*");
      const url = line.text.includes("https://");
      context.font = `${heading || keyed ? "700" : "400"} ${heading ? "27" : "24"}px Arial`;
      context.fillStyle = heading ? "#337895" : url ? "#087f5b" : "#111827";
      context.fillText(line.text, padding, y);
      y += 34;
    });
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not create image")), "image/png");
  });
}

function downloadImageBlob(blob, booking) {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.download = `Booking-${booking?.bookingId || "Trip"}-Day-Wise-Plan.png`;
  link.href = url;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function drawDayColumn(context, day, x, y, width, height) {
  context.fillStyle = "#f8fbfd";
  context.strokeStyle = "#d9e6ec";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(x, y, width, height, 12);
  context.fill();
  context.stroke();

  let cursorY = y + 42;
  context.fillStyle = "#337895";
  context.font = "700 28px Arial";
  context.fillText(String(day.day).toUpperCase(), x + 28, cursorY);
  cursorY += 38;
  context.font = "700 27px Arial";
  wrapCanvasText(day.title, 32).forEach((line) => {
    context.fillText(line, x + 28, cursorY);
    cursorY += 34;
  });
  cursorY += 12;
  context.fillStyle = "#111827";
  context.font = "24px Arial";
  day.locations.forEach((location) => {
    const lines = wrapCanvasText(location, 34);
    context.fillText("•", x + 28, cursorY);
    lines.forEach((line, index) => {
      context.fillText(line, x + 55, cursorY + index * 34);
    });
    cursorY += Math.max(1, lines.length) * 34;
  });
}

function wrapCanvasText(value, maximumCharacters) {
  const words = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maximumCharacters && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
}

async function copyText(text, successMessage) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.error("Could not copy text");
  }
}

function formatDurationBetween(start, end) {
  const startTime = new Date(start || 0).getTime();
  const endTime = new Date(end || 0).getTime();
  if (!startTime || !endTime || endTime < startTime) return "-";
  const totalMinutes = Math.round((endTime - startTime) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours} hr ${minutes} min`;
  if (hours) return `${hours} hr`;
  return `${minutes} min`;
}
